import { Loading } from "../../../lib/loading.tsx";
import { subjectBy } from "./subject.ts";
import { catalogText, totalProblemText } from "./topic-picks-text.ts";
import { TopicRow } from "./TopicRow.tsx";
import type { TopicPicks as TopicPicksState } from "./use-topic-picks.ts";

/**
 * Ветка «По темам»: строки-пики и общий счётчик (5.5 docs/homework-phase2-design.md).
 *
 * Задания у каждого ученика свои: генератор бросает своё зерно на каждую работу.
 * Именно поэтому одна тема на двенадцать человек это одно нажатие, а не
 * двенадцать заходов в форму.
 *
 * ⚠️ Ветка одна на оба предмета, предмет приезжает состоянием: справочники у
 * математики и английского разные, а правила счёта, потолков и предпросмотра
 * общие до последней строки (`@edu/shared`, `topic-form.ts`). Второй компонент
 * ради второго предмета означал бы два места, где правило потолка однажды
 * разъедется.
 *
 * ⚠️ Стоит у двух экранов, у формы выдачи домашки и у пачки заданий на уроке
 * (раздел 7.1 `docs/room-exercise-batch-design.md`), и место едет в состоянии
 * (`state.place`), а не отдельным свойством: потолок и слова выводятся из него
 * в одном месте. Модуль не ходит в форму домашки ни одним импортом, иначе
 * комната урока потащила бы её за собой.
 */
export function TopicPicks(props: { state: TopicPicksState; busy: boolean }) {
  const { state } = props;
  const by = subjectBy(state.subject);

  if (state.failed) {
    return (
      <p className="err">
        {catalogText("failed", state.place, by)}{" "}
        <button type="button" className="btn btn--quiet" onClick={state.reload}>
          Повторить
        </button>
      </p>
    );
  }

  if (state.topics === null) return <Loading />;

  if (state.topics.length === 0) {
    // У урока текста нет: ветка там мёртвая, разбор у `catalogText`
    const empty = catalogText("empty", state.place, by);
    return empty === null ? null : <p className="muted">{empty}</p>;
  }

  /*
    Перебор общего потолка у урока сказан ОДИН раз, у счётчика: `rowProblem`
    отдаёт `over-total` каждой годной строке, а виновата сумма. У домашки
    `totalProblemText` пуст, там текст стоит у строки, как стоял
  */
  const overTotal = state.rows.some((r) => state.problemOf(r)?.kind === "over-total")
    ? totalProblemText(state.place, state.hardCap)
    : null;

  return (
    <>
      <ul className="picks__list">
        {state.rows.map((row) => (
          <TopicRow
            key={row.rowId}
            subject={state.subject}
            place={state.place}
            topics={state.topics ?? []}
            row={row}
            merged={state.mergedOf(row)}
            max={state.maxOf(row)}
            problem={state.problemOf(row)}
            removable={state.rows.length > 1}
            busy={props.busy}
            onSkill={(skill) => state.setSkill(row.rowId, skill)}
            onDifficulty={(difficulty) => state.setDifficulty(row.rowId, difficulty)}
            onCount={(count) => state.setCount(row.rowId, count)}
            onRemove={() => state.remove(row.rowId)}
          />
        ))}
      </ul>

      <p className="acts">
        <button type="button" className="btn btn--quiet" disabled={props.busy} onClick={state.add}>
          + добавить тему
        </button>
        <span className="muted">
          всего заданий: {state.total} из {state.hardCap}
        </span>
      </p>
      {overTotal !== null && <p className="err picks__note">{overTotal}</p>}
    </>
  );
}
